import _ from 'lodash';

import {
  RunHistoryQueryQuery,
  RunHistoryQueryQueryVariables,
} from '../../../generated/graphql';
import {RUN_HISTORY_QUERY} from '../../../state/graphql/runHistoryQuery';
import {RunHistoryRow, RunSignature} from '../../../types/run';
import {fetchGQL} from './util';

type EnsureHistoryRowsParams = FetchRunHistoryParams & {
  historyRowByStep: Map<number, RunHistoryRow>;
};

// Mutate a step -> history row mapping by adding missing steps within a certain range
export async function ensureHistoryRows({
  historyRowByStep,
  runSignature,
  historyKeys,
  minStep,
  maxStep,
}: EnsureHistoryRowsParams): Promise<void> {
  const historyRows = await fetchUnsampledRunHistory({
    runSignature,
    historyKeys,
    minStep,
    maxStep,
  });
  if (historyRows == null) {
    return;
  }
  for (const r of historyRows) {
    if (historyRowByStep.has(r._step)) {
      continue;
    }
    historyRowByStep.set(r._step, r);
  }
}

// This should match the backend sampling limit.
// TODO: make resilient to desync from source of truth
const MAX_SAMPLE_SIZE = 10000;

type FetchRunHistoryParams = {
  runSignature: RunSignature;
  historyKeys: string[];
  minStep?: number;
  maxStep?: number;
};

// Fetch history for a specific range of steps. As long as the range is smaller
// than the max sample size, we'll get all of the logged steps unsampled.
// TODO: be smarter than just assuming that maxStep - minStep <= maxSampleSize
// TODO: paginate when maxStep - minStep > maxSampleSize
export async function fetchUnsampledRunHistory({
  runSignature,
  historyKeys,
  minStep,
  maxStep,
}: FetchRunHistoryParams): Promise<RunHistoryRow[] | null> {
  const data = await fetchGQL<
    RunHistoryQueryQueryVariables,
    RunHistoryQueryQuery
  >({
    query: RUN_HISTORY_QUERY,
    variables: {
      ...runSignature,
      sampledHistorySpecs: [
        JSON.stringify({
          keys: _.uniq([`_step`, ...historyKeys]),
          samples: MAX_SAMPLE_SIZE,
          minStep,
          maxStep,
        }),
      ],
    },
  });
  return data?.project?.run?.sampledHistory?.[0] ?? null;
}
